import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, Zap, Shield, BarChart3, Cpu, Layers,
  ArrowRight, Check, PieChart, Activity, AlertTriangle,
} from "lucide-react";
import NewsletterOptin from "../components/NewsletterOptin";
import DiscordCTA from "../components/DiscordCTA";
import { usePublicLang, LangToggle } from "../lib/publicLang";
import LandingEn from "./LandingEn";

function TrustBadge() {
  const [metrics, setMetrics] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reporting/trust-badge`)
      .then((r) => r.json())
      .then((d) => alive && setMetrics(d))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!metrics) {
    return (
      <div
        className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-white/40"
        data-testid="trust-badge-loading"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-pulse" />
        Chargement des métriques de significativité…
      </div>
    );
  }

  const isInstit = metrics.verdict === "solid";
  const fmtPct = (v) => (v == null ? "N/A" : `${v}%`);
  return (
    <div
      className="mt-8 inline-flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-up/30 bg-up/5 px-5 py-3 text-xs backdrop-blur"
      data-testid="trust-badge"
      title="Rapport de démonstration — méthodologie calculée sur un jeu de données d'exemple (seed reproductible). Formules statistiques réelles : sign-flip, PBO, DSR. Vos rapports utiliseront vos données réelles."
    >
      <div className="flex items-center gap-2 pr-3 border-r border-white/10">
        <span className={`w-2 h-2 rounded-full ${isInstit ? "bg-up animate-pulse" : "bg-yellow-400"}`} />
        <span className="uppercase tracking-widest text-[10px] font-semibold text-up">
          Démo Méthodologique
        </span>
      </div>
      <TrustMetric label="Sharpe" value={metrics.avg_sharpe} testId="trust-sharpe" />
      <TrustMetric label="Profit Factor" value={metrics.avg_profit_factor} testId="trust-pf" />
      <TrustMetric label="DSR (edge réel)" value={fmtPct(metrics.dsr_prob_pct)} good={metrics.dsr_prob_pct != null && metrics.dsr_prob_pct > 90} testId="trust-dsr" />
      <TrustMetric label="PBO (overfit)" value={fmtPct(metrics.pbo_pct)} good={metrics.pbo_pct != null && metrics.pbo_pct < 30} testId="trust-pbo" />
      <TrustMetric label={`Sig. p<5% (${metrics.n_symbols})`} value={metrics.n_significant_5pct} testId="trust-sig5" />
      {metrics.is_simulated && (
        <div className="w-full pt-1.5 mt-0.5 border-t border-white/10 text-[10px] text-yellow-400/90" data-testid="trust-simulated-label">
          📊 Démonstration de la méthodologie sur un jeu de données d&apos;exemple — vos rapports seront calculés sur vos données réelles importées
        </div>
      )}
    </div>
  );
}

function TrustMetric({ label, value, good, testId }) {
  const color = good === undefined ? "text-white" : good ? "text-up" : "text-yellow-400";
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-white/40 uppercase tracking-widest text-[9px]">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}

function LiveStatsRibbon() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reporting/live-stats`)
        .then((r) => r.json())
        .then((d) => alive && setStats(d))
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!stats) return null;
  const a = stats.attestation || {};
  const hasLat = a.latency_p50_ms != null;
  const fmtLat = (v) => v == null ? "—" : `${v}ms`;
  const l2Live = a.l2_source && a.l2_source !== "synthetic";

  return (
    <div className="mt-6 grid gap-3 lg:grid-cols-2" data-testid="live-stats-ribbon">
      {/* LINE 1 — Retail social proof (honest, real MongoDB counts) */}
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-xs backdrop-blur"
        data-testid="live-stats-retail"
      >
        <div className="flex items-center gap-2 pr-3 border-r border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-blue animate-pulse" />
          <span className="text-[9px] uppercase tracking-widest text-white/50">Activité plateforme</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-signals-today">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">Signaux aujourd&apos;hui</span>
          <span className="font-mono font-semibold text-white">{stats.signals_today.toLocaleString("fr-FR")}</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-signals-7d">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">7 jours</span>
          <span className="font-mono font-semibold text-white">{stats.signals_7d.toLocaleString("fr-FR")}</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-raw-exports">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">Exports données brutes</span>
          <span className="font-mono font-semibold text-white">{(stats.raw_data_exports ?? 0).toLocaleString("fr-FR")}</span>
        </div>
      </div>

      {/* LINE 2 — Institutional attestation (what a top-0.1% quant PM actually cares about) */}
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-xs backdrop-blur font-mono"
        data-testid="live-stats-attestation"
        title="Attestation temps réel — vérifiable via /api/audit-export et /api/health"
      >
        <div className="flex items-center gap-2 pr-3 border-r border-white/10">
          <span className={`w-1.5 h-1.5 rounded-full ${l2Live ? "bg-up" : "bg-yellow-400"} animate-pulse`} />
          <span className="text-[9px] uppercase tracking-widest text-white/50">Attestation live</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-latency">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">p50/95/99</span>
          {hasLat ? (
            <span className="text-white">{fmtLat(a.latency_p50_ms)}/{fmtLat(a.latency_p95_ms)}/{fmtLat(a.latency_p99_ms)}</span>
          ) : (
            <span className="text-white/40 italic">échantillonnage…</span>
          )}
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-uptime">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">Uptime</span>
          <span className="text-white">{a.uptime_hours}h</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-errors">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">Err</span>
          <span className={a.error_rate_pct < 0.1 ? "text-up" : "text-yellow-400"}>{a.error_rate_pct}%</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-audit-hash">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">Audit SHA-256</span>
          <Link
            to="/audit"
            className="text-accent-blue hover:text-cyan-300 underline decoration-dotted underline-offset-2"
            title={`${a.audit_chain_last_hash || "no chain yet"} — Cliquez pour vérifier la chaîne complète`}
          >
            {a.audit_chain_last_hash || "genesis"}
          </Link>
        </div>
        <div className="flex items-center gap-1.5" data-testid="live-l2-provider">
          <span className="text-white/40 uppercase tracking-widest text-[9px]">L2</span>
          <span className={l2Live ? "text-up" : "text-white/60"}>
            {a.l2_source === "real_databento" ? "Databento MBP-10" :
             a.l2_source === "real_polygon" ? "Polygon NBBO" :
             "Synthetic (fallback)"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [lang, setLang] = usePublicLang();
  if (lang === "en") return <LandingEn lang={lang} setLang={setLang} />;
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="landing-page">
      {/* AMF/ESMA regulatory disclosure banner — always visible */}
      <div className="bg-red-950/60 border-b border-red-500/30 text-red-200 text-[11px] px-6 py-2 text-center" data-testid="amf-top-banner">
        <span className="font-semibold">⚠️ AVERTISSEMENT</span> · ANATRADEX est un <b>outil d'analyse statistique</b> et NE fournit PAS de conseil en investissement. Le trading comporte des risques de perte substantielle — 70-85% des traders retail perdent selon l'ESMA. ANATRADEX n'est pas enregistré CIF/ORIAS.
      </div>
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2" data-testid="landing-brand">
            <img src="/favicon.svg" alt="ANATRADEX logo" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest hidden sm:block">Outil d'analyse statistique</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 sm:gap-4">
            <Link to="/academy" className="text-sm text-white/70 hover:text-white transition hidden md:inline" data-testid="landing-academy-link">Academy</Link>
            <Link to="/methodology" className="text-sm text-white/70 hover:text-white transition hidden lg:inline" data-testid="landing-methodology-link">Methodology</Link>
            <Link to="/audit" className="text-sm text-white/70 hover:text-white transition hidden sm:flex items-center gap-1.5" data-testid="landing-audit-link">
              <span className="w-1.5 h-1.5 rounded-full bg-up animate-pulse" /> Audit public
            </Link>
            <Link to="/pricing" className="text-sm text-white/70 hover:text-white transition hidden sm:inline" data-testid="landing-pricing-link">Tarifs</Link>
            <Link to="/beta" className="text-sm text-warn hover:text-white transition hidden lg:inline" data-testid="landing-beta-link">Beta (sur invitation)</Link>
            <Link to="/login" className="text-sm text-white/70 hover:text-white transition hidden sm:inline" data-testid="landing-login-link">Connexion</Link>
            <Link to="/signup" className="btn btn-primary whitespace-nowrap" data-testid="landing-cta-signup">
              <span className="sm:hidden">Essai 14j</span><span className="hidden sm:inline">Essai gratuit 14j</span> <ArrowRight size={14} />
            </Link>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-blue/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-6">
            <div className="w-2 h-2 rounded-full bg-accent-blue animate-pulse" />
            Pour prop-traders sérieux, gérants indépendants & devs quant
          </div>
          <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            L'outil d'analyse<br />
            <span className="bg-gradient-to-r from-accent-blue to-cyan-300 bg-clip-text text-transparent">
              que le trading sérieux mérite enfin.
            </span>
          </h1>
          <p className="mt-8 text-lg text-white/60 max-w-2xl leading-relaxed">
            Analyse statistique et tests de robustesse (PBO, Deflated Sharpe) sur <b className="text-white">indices, actions, crypto, matières premières et forex</b>.
            Backtest event-driven, ML calibré, attestation cryptographique d&apos;intégrité.
            Un outil libre-service pour <b className="text-white">tester vos propres stratégies</b>,
            passer du <span className="italic">« ça marche sur mon écran »</span> au <span className="italic">« statistiquement significatif »</span>.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/signup" className="btn btn-primary text-base px-6 py-3" data-testid="hero-cta-signup">
              14 jours d'essai gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className="btn text-base px-6 py-3" data-testid="hero-cta-pricing">
              Voir les tarifs
            </Link>
            <Link to="/app" className="text-sm text-white/60 hover:text-white ml-2" data-testid="hero-cta-demo">
              Essayer la démo (sans compte) →
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap gap-8 text-sm text-white/60">
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> Aucune CB requise pour l'essai</div>
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> Résiliation en 1 clic</div>
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> Multi-langue + multi-devise</div>
          </div>
          <TrustBadge />
          <LiveStatsRibbon />
        </div>
      </section>

      {/* METRICS */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: "50+", label: "Symboles Forex, Crypto, Indices" },
            { value: "8", label: "Langues (RTL incluses)" },
            { value: "100 %", label: "Métriques reproductibles (seed public)" },
            { value: "SHA-256", label: "Journal d'audit hash-chainé" },
          ].map((m, i) => (
            <div key={i}>
              <div className="font-heading text-4xl font-bold bg-gradient-to-br from-white to-white/50 bg-clip-text text-transparent">{m.value}</div>
              <div className="text-xs text-white/50 uppercase tracking-widest mt-2">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="mb-16">
          <div className="text-xs uppercase tracking-widest text-accent-blue mb-4">Ce qui nous distingue</div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight max-w-3xl">
            La rigueur statistique qui manque à 99 % des plateformes retail.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: "Exécution paper réaliste", desc: "Slippage, spread, latence, market impact — comme sur un vrai LOB institutionnel." },
            { icon: Layers, title: "Order Flow Level-2", desc: "Volume Profile (POC/VAH/VAL), OFI Cont-Kukanov-Stoikov, VWAP z-score, absorption events." },
            { icon: BarChart3, title: "Backtest event-driven", desc: "Vraie latence réseau + exchange, queue position, causal ordering — pas de simple boucle OHLC." },
            { icon: Cpu, title: "ML entraîné + validé", desc: "Purged K-Fold CV, Deflated Sharpe, permutation tests, White's Reality Check. Pas de hardcoded rules." },
            { icon: Shield, title: "Risk & Compliance", desc: "Pre-trade engine, kill-switch qui flatten les positions, exports CSV/JSON à titre démonstratif." },
            { icon: PieChart, title: "Allocation régime-switching", desc: "Kelly fractionnaire multi-actifs, corrélation-ajusté, régime detection ADX + Hurst." },
          ].map((f, i) => (
            <div key={i} className="panel p-6 hover:border-accent-blue/50 transition-colors group">
              <f.icon className="text-accent-blue mb-4 group-hover:scale-110 transition-transform" size={28} />
              <div className="font-heading font-semibold text-lg mb-2">{f.title}</div>
              <div className="text-sm text-white/60 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STAT PROOF */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-blue mb-4">Statistical Significance</div>
            <h2 className="font-heading font-bold text-4xl tracking-tight">
              Si ton edge n'est pas statistiquement prouvé, ce n'est pas un edge.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              La plupart des backtests retail affichent des Sharpe {'>'} 3 issus d'un overfitting massif.
              Nous testons chaque stratégie avec les mêmes standards statistiques que la recherche quantitative institutionnelle :
              Bootstrap CI, permutation tests, Deflated Sharpe (Bailey-López de Prado), et White's Reality Check.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Deflated Sharpe qui corrige le multiple testing bias",
                "Purged K-Fold CV pour éliminer le look-ahead bias",
                "Bootstrap 1000+ pour vraies confidence intervals",
                "Permutation tests contre H₀ = alpha nul",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={16} className="text-up mt-0.5 flex-shrink-0" />
                  <span className="text-white/80">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel p-8 font-mono text-xs">
            <div className="text-white/40 mb-3">// Rapport de significativité en direct</div>
            <div className="space-y-2 text-white/80">
              <div>Sharpe observé : <span className="text-accent-blue">2.14</span></div>
              <div>Bootstrap CI 95% : <span className="text-accent-blue">[1.42, 2.87]</span></div>
              <div>Deflated Sharpe P : <span className="text-up">0.983</span> ✓ significatif à 5%</div>
              <div>Permutation p-value : <span className="text-up">0.008</span> ✓ significatif à 1%</div>
              <div className="pt-3 border-t border-white/10 text-white/60">
                → Edge probablement RÉEL (pas overfit)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTITUTIONAL DD PACK */}
      <section className="border-t border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-up animate-pulse"></span>
                POUR PROP FIRMS & TRADERS EXIGEANTS
              </div>
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-tight">
                Le rapport statistique<br />
                <span className="text-up">qui prouve votre rigueur.</span>
              </h2>
              <p className="mt-6 text-lg text-white/70">
                Rapport PDF 4 pages généré automatiquement : Sharpe OOS, Probability of Backtest
                Overfitting (PBO), Deflated Sharpe (Bailey-López de Prado), permutation p-value,
                stress tests, méthodologie complète, attestation d'intégrité SHA-256.
              </p>
              <ul className="mt-8 space-y-3 text-white/80">
                <li className="flex items-start gap-3">
                  <span className="text-up mt-1">▸</span>
                  <span><b>Executive Summary</b> — Sharpe, PF, Max DD, PBO, DSR sur 5 symboles majeurs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-up mt-1">▸</span>
                  <span><b>Backtests par symbole</b> — EURUSD, GBPUSD, XAUUSD, BTCUSD, SPX500 avec p-value permutation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-up mt-1">▸</span>
                  <span><b>Statistical robustness</b> — PBO &lt; 30% + DSR probability à démontrer un vrai edge</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-up mt-1">▸</span>
                  <span><b>Integrity attestation</b> — SHA-256 + hash-chain audit vérifiable</span>
                </li>
              </ul>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/reporting/dd-pack?use_real_trades=false`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary text-base px-8 py-4"
                  data-testid="dd-pack-download-btn"
                  onClick={(e) => {
                    if (!localStorage.getItem("anatradex-report-ack")) {
                      e.preventDefault();
                      const ok = window.confirm(
                        "AVERTISSEMENT IMPORTANT\n\n" +
                        "Ceci est un RAPPORT DE DÉMONSTRATION : il illustre la méthodologie et le format sur un jeu de données d'exemple — pas des performances réelles.\n\n" +
                        "Il NE constitue PAS :\n" +
                        "  • un conseil en investissement\n" +
                        "  • une certification de stratégie\n" +
                        "  • une promesse de performance future\n\n" +
                        "70-85% des traders retail perdent selon l'ESMA.\n" +
                        "ANATRADEX n'est pas enregistré CIF.\n\n" +
                        "Cochez OK si vous comprenez que ce rapport est un outil d'analyse et non une garantie."
                      );
                      if (ok) {
                        localStorage.setItem("anatradex-report-ack", "1");
                        window.open(e.currentTarget.href, "_blank");
                      }
                    }
                  }}
                >
                  📥 Télécharger un extrait (PDF)
                </a>
                <Link to="/auth" className="btn text-base px-8 py-4" data-testid="dd-pack-signup-btn">
                  Générer avec mes trades →
                </Link>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/reporting/dd-pack-data?use_real_trades=false&format=csv`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn text-base px-8 py-4"
                  data-testid="dd-pack-raw-data-btn"
                >
                  ⬇ Données brutes (CSV)
                </a>
              </div>
              <p className="mt-4 text-xs text-white/40">
                L&apos;extrait est un <b>rapport de démonstration</b> : il illustre la méthodologie et le
                format que vous recevrez, calculé sur un jeu de données d&apos;exemple (seed reproductible
                affiché dans le rapport). Chaque chiffre est vérifiable via l&apos;export de données brutes.
                Créez un compte pour générer un rapport à partir de vos propres trades.
              </p>
            </div>

            {/* Preview mockup + LIVE PDF embed */}
            <div className="relative">
              <div className="relative rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-4 shadow-2xl">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-xs text-white/40 truncate">ANATRADEX_Analyse_Statistique.pdf</div>
                  <div className="rounded-full bg-up text-black text-[10px] font-bold px-2 py-0.5">
                    LIVE · PDF · 4p
                  </div>
                </div>
                <iframe
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/reporting/dd-pack?use_real_trades=false&symbols=EURUSD,XAUUSD,BTCUSD&disposition=inline#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-[520px] rounded-lg border border-white/5 bg-white"
                  title="ANATRADEX Rapport d'Analyse Statistique — Aperçu"
                  data-testid="dd-pack-inline-preview"
                />
                <div className="mt-3 flex items-center gap-2 text-[10px] text-white/40 px-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-up animate-pulse"></span>
                  Rendu en direct depuis notre backend · SHA-256 vérifiable
                </div>
              </div>
              <div className="absolute -inset-2 -z-10 rounded-3xl bg-gradient-to-tr from-up/20 to-transparent blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 text-center border-t border-white/10">
        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">
          Prêt à passer au niveau supérieur ?
        </h2>
        <p className="mt-6 text-lg text-white/60">
          Rejoins les prop-traders sérieux qui utilisent ANATRADEX pour prouver leur edge — statistiquement.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/pricing" className="btn btn-primary text-base px-8 py-4" data-testid="footer-cta-pricing">
            Voir les tarifs
          </Link>
          <Link to="/app" className="btn text-base px-8 py-4" data-testid="footer-cta-demo">
            Essayer la démo
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6">
          {/* NEWSLETTER — capture pre-signup leads */}
          <div className="mb-10 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="landing-newsletter">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-accent-blue mb-2">Restez informé sans compte</div>
                <div className="font-heading text-xl font-semibold mb-1">1 email/mois — méthodologie quant + updates</div>
                <div className="text-sm text-white/60">Nouveaux articles Academy, PBO/DSR breakdowns, cas d&apos;usage prop firm. Aucun spam, aucune revente, désinscription en 1 clic.</div>
              </div>
              <NewsletterOptin source="landing-footer" />
            </div>
          </div>
          {/* LEGAL RISK WARNING — mandatory */}
          <div className="mb-10 rounded-xl border-2 border-warn/40 bg-warn/5 p-5" data-testid="landing-risk-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-warn shrink-0 mt-0.5" size={20} />
              <div className="text-xs text-white/70 leading-relaxed">
                <b className="text-warn uppercase tracking-widest">Avertissement de risques</b> — Le trading sur les marchés financiers
                (Forex, indices, crypto-actifs, matières premières, actions) présente un <b>risque de perte substantiel pouvant atteindre 100% du capital investi</b>, voire au-delà en cas d&apos;effet de levier. Selon les régulateurs européens (ESMA), <b>70 à 85 % des traders retail perdent de l&apos;argent</b>. ANATRADEX est un <b>outil d&apos;analyse et d&apos;apprentissage</b> en libre-service, non un service de conseil en investissement. ANATRADEX n&apos;est <b>pas</b> enregistré comme Conseiller en Investissements Financiers (CIF) auprès de l&apos;AMF/ORIAS.
                les métriques affichées (Sharpe, PBO, DSR) sont calculées sur des données de backtest et de démonstration — <b>elles ne garantissent en aucun cas la performance future</b>. Ne tradez que du capital que vous pouvez vous permettre de perdre. <Link to="/risk-disclosure" className="text-accent-blue underline">Lire la déclaration complète</Link>.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 text-sm">
            <div>
              <div className="text-white/40 uppercase tracking-widest text-[10px] mb-3">Produit</div>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/pricing" className="hover:text-white">Tarifs</Link></li>
                <li><Link to="/audit" className="hover:text-white">Audit public</Link></li>
                <li><Link to="/methodology" className="hover:text-white">Méthodologie</Link></li>
                <li><Link to="/lexique" className="hover:text-white">Lexique</Link></li>
                <li><Link to="/plateformes" className="hover:text-white">MT4 / MT5 / cTrader / TradingView</Link></li>
                <li><Link to="/academy" className="hover:text-white">Academy</Link></li>
                <li><Link to="/beta" className="hover:text-white">Programme beta</Link></li>
                <li><Link to="/signup" className="hover:text-white">Essai gratuit</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-widest text-[10px] mb-3">Légal</div>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/risk-disclosure" className="hover:text-white">Déclaration de risques</Link></li>
                <li><Link to="/terms" className="hover:text-white">CGU</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Confidentialité</Link></li>
                <li><Link to="/cookies" className="hover:text-white">Cookies</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-widest text-[10px] mb-3">Ressources</div>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/academy/trading-algorithmique-101" className="hover:text-white">Trading algo 101</Link></li>
                <li><Link to="/academy/deflated-sharpe-explique" className="hover:text-white">Deflated Sharpe</Link></li>
                <li><Link to="/academy/backtest-piege-overfitting" className="hover:text-white">Overfitting</Link></li>
                <li><Link to="/academy/prop-firm-erreurs-a-eviter" className="hover:text-white">Prop firm 101</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-widest text-[10px] mb-3">Contact</div>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/contact" className="hover:text-white font-medium">📝 Formulaire de contact</Link></li>
                <li><a href="mailto:contact@anatradex.com" className="hover:text-white">contact@anatradex.com</a></li>
                <li><a href="mailto:legal@anatradex.com" className="hover:text-white">legal@anatradex.com</a></li>
                <li><a href="mailto:privacy@anatradex.com" className="hover:text-white">privacy@anatradex.com</a></li>
              </ul>
              <div className="mt-3 rounded-lg border border-up/20 bg-up/5 px-3 py-2 text-[11px] text-white/70" data-testid="footer-support-direct">
                <span className="text-up font-semibold">Support direct fondateur</span> — réponse généralement sous 48h ouvrées
              </div>
              <div className="mt-4">
                <div className="text-white/40 uppercase tracking-widest text-[10px] mb-2">Communauté</div>
                <DiscordCTA variant="default" className="w-full justify-center" />
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40">
            <div>© 2026 ANATRADEX — Outil d'analyse statistique pour traders. ANATRADEX n'est pas un Conseiller en Investissements Financiers (CIF).</div>
            <div>Le trading comporte un risque de perte en capital. Non destiné aux résidents des USA.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
